/**
 * What must never leave the browser in a log, whatever carries it.
 *
 * Sentry sees more than the code that calls it: a captured Axios error drags
 * its request configuration, its headers and its response body along, and the
 * breadcrumb trail records every URL the app asked for. The contract forbids
 * Bearer tokens, Nota text, free motivations, phone numbers and email addresses
 * from appearing in any of it, so the redaction is applied to the whole event
 * rather than to the few places sensitive values are expected in.
 */

const REDACTED = '[redacted]';

/**
 * Keys carrying content the contract protects. These are matched exactly:
 * `note` is the Nota, `noteCount` is not, and blanking the second would cost
 * the report a fact worth having.
 *
 * `email` is on the list because a lead's contact email arrives under it. The
 * operator's own identity is not lost with it: Sentry still reports the session
 * by IP and by the ids it collects on its own.
 */
const SENSITIVE_CONTENT_KEYS = new Set([
  'note',
  'notes',
  'motivation',
  'motivazione',
  'reason',
  'phone',
  'telefono',
  'email',
  'search',
]);

/**
 * Credential markers, matched anywhere in the key. A secret has no reason to be
 * in a log under any spelling, so `Authorization`, `accessToken` and the
 * `tassoQuintoAccessToken` the request interceptor leaves on its own config are
 * all caught without having to be enumerated one by one.
 */
const CREDENTIAL_KEY_MARKERS = [
  'authorization',
  'accesstoken',
  'refreshtoken',
  'idtoken',
  'bearer',
  'cookie',
  'password',
  'secret',
  'apikey',
];

/**
 * Query parameters whose value is the operator's search text. On Prestiti that
 * text is a name, an email or a phone number, so a URL is not safe to log until
 * they are gone — and a URL travels in breadcrumbs, in messages and in stack
 * frames alike.
 */
const SENSITIVE_QUERY_PARAMS = new Set(['search', 'q', 'email', 'phone']);

const BEARER_PATTERN = /\b(Bearer\s+)[A-Za-z0-9._~+/-]+=*/gi;

/** A JWT written out on its own, without the scheme in front of it. */
const JWT_PATTERN =
  /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9._~+/-]+=*/g;

const EMAIL_PATTERN = /\b[^\s@<>()[\]]+@[^\s@<>()[\]]+\.[A-Za-z]{2,}\b/g;

/** Case and separators carry no meaning here: `Set-Cookie` is `setcookie`. */
const normalizeKey = (key: string) =>
  key.toLocaleLowerCase('en-US').replace(/[^a-z0-9]/g, '');

const isSensitiveKey = (key: string) => {
  const normalized = normalizeKey(key);
  return (
    SENSITIVE_CONTENT_KEYS.has(normalized) ||
    CREDENTIAL_KEY_MARKERS.some((marker) => normalized.includes(marker))
  );
};

/**
 * Strips the search-bearing parameters out of every URL in a string, leaving
 * page, product and status filters legible: a breadcrumb that cannot say which
 * page failed is not worth keeping.
 */
const redactUrlQuery = (value: string) =>
  value.replace(/[^\s"'<>]*\?[^\s"'<>]*/g, (candidate) => {
    const [path, ...queryParts] = candidate.split('?');
    const query = queryParts.join('?');
    if (!query) return candidate;

    // Rebuilt by hand rather than through URLSearchParams: the parameters that
    // survive must read exactly as they were logged, not re-encoded.
    const redacted = query
      .split('&')
      .map((pair) => {
        const separator = pair.indexOf('=');
        if (separator < 0) return pair;

        const name = pair.slice(0, separator);
        return SENSITIVE_QUERY_PARAMS.has(name.toLocaleLowerCase('en-US'))
          ? `${name}=${REDACTED}`
          : pair;
      })
      .join('&');

    return redacted === query ? candidate : `${path}?${redacted}`;
  });

export const redactSensitiveText = (value: string) =>
  redactUrlQuery(value)
    .replace(BEARER_PATTERN, `$1${REDACTED}`)
    .replace(JWT_PATTERN, REDACTED)
    .replace(EMAIL_PATTERN, REDACTED);

/**
 * Rewrites a value with every sensitive key blanked and every string scrubbed.
 * Structure is preserved so a redacted event still reads as the event it was:
 * knowing that a Nota was rejected is the point, knowing what it said is not.
 *
 * Cyclic references are left in place as a marker rather than followed, and the
 * walk stops at a depth no honest payload reaches.
 */
const MAX_DEPTH = 12;

export const redactSensitiveData = <T>(value: T): T => {
  const seen = new WeakSet<object>();

  const walk = (current: unknown, depth: number): unknown => {
    if (typeof current === 'string') return redactSensitiveText(current);
    if (current === null || typeof current !== 'object') return current;
    if (depth >= MAX_DEPTH) return REDACTED;
    if (seen.has(current)) return '[circular]';
    seen.add(current);

    if (Array.isArray(current)) {
      return current.map((entry) => walk(entry, depth + 1));
    }

    return Object.fromEntries(
      Object.entries(current as Record<string, unknown>).map(([key, entry]) => [
        key,
        isSensitiveKey(key) ? REDACTED : walk(entry, depth + 1),
      ])
    );
  };

  return walk(value, 0) as T;
};
